/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-filename-extension */
import { assert } from '@bingads-webui-campaign-react/debug-assert';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Dropdown, IconButton, IDropdownOption, Label, IDropdownProps } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import React, { FC, useContext } from 'react';
import { paginatorStyleClassName } from './paginator.styles';

export interface PaginatorProps {
  className?: string;
  /**
   * @default _TL_("Show rows:")
   */
  pageSizeLabel?: string;
  /**
   * @default [10,20,50]
   */
  pageSizeOptions?: number[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageIndexChange(pageNum: number): void;
  onPageSizeChange(pageSize: number): void;
}

function buildPageSizeOptions(selectedPageSize: number, pageSizeOptions: number[]): IDropdownOption<number>[] {
  assert(pageSizeOptions.includes(selectedPageSize));
  return pageSizeOptions.map(pageSize => ({
    key: pageSize,
    text: pageSize.toString(),
    selected: selectedPageSize === pageSize,
    data: pageSize,
  }));
}

function buildPageIndexOptions(selectedPageIndex: number, pageIndexOptions: number[]): IDropdownOption<number>[] {
  assert(pageIndexOptions.includes(selectedPageIndex));
  return pageIndexOptions.map(pageIndex => ({
    key: pageIndex,
    text: pageIndex.toString(),
    selected: selectedPageIndex === pageIndex,
    data: pageIndex,
  }));
}

export const Paginator: FC<PaginatorProps> = (props) => {
  const {
    className,
    currentPage,
    onPageIndexChange,
    onPageSizeChange,
    pageSize,
    totalCount,
    pageSizeLabel = _TL_('Show rows:'),
    pageSizeOptions = [10, 20, 50],
  } = props;
  assert(currentPage >= 1);
  const i18n: any = useContext(I18nContext);
  const pageCount = Math.ceil(totalCount / pageSize);

  const pageSizeSelectorId = useId('page-size-selector');
  const pageSizeSelectorOptions = buildPageSizeOptions(pageSize, pageSizeOptions);
  const pageIndexSelectorOptions = buildPageIndexOptions(currentPage, Array.from(Array(pageCount), (_, i) => i + 1));

  const PREVIOUS_BUTTON = i18n.getString(_TL_('Previous page'));
  const NEXT_BUTTON = i18n.getString(_TL_('Next page'));

  function pageIndexChangeHandler(newPageIndex: number) {
    onPageIndexChange(newPageIndex);
  }

  function onPageSizeSelectorChange(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<any>) {
    const newPageSize = option.data;
    onPageSizeChange(newPageSize);

    // when change page size, the currentPageIndex might be out of range. If so, set it to the new last page.
    const newMaxPageNum = Math.ceil(totalCount / newPageSize);
    if (currentPage > newMaxPageNum) {
      pageIndexChangeHandler(newMaxPageNum);
    }
  }

  const onPageIndexSelectorChange: IDropdownProps['onChange'] = (event, option) => {
    pageIndexChangeHandler(option.data);
  };

  return (
    <div className={`${className} ${paginatorStyleClassName}`}>
      <div className="page-size-selector">
        <Label htmlFor={pageSizeSelectorId}>{i18n.getString(pageSizeLabel)}</Label>
        <Dropdown className="page-size-selector-dropdown" id={pageSizeSelectorId} onChange={onPageSizeSelectorChange} options={pageSizeSelectorOptions} />
      </div>
      <div className="page-index-selector">
        <IconButton
          className="page-change-icon previous-page"
          iconProps={{ iconName: 'ChevronLeft' }}
          disabled={currentPage === 1}
          title={PREVIOUS_BUTTON}
          aria-label={PREVIOUS_BUTTON}
          onClick={() => { pageIndexChangeHandler(currentPage - 1); }}
        />
        <Dropdown className="page-index-selector-dropdown" onChange={onPageIndexSelectorChange} selectedKey={currentPage} options={pageIndexSelectorOptions} />
        <span>{` / ${pageCount}`}</span>
        <IconButton
          className="page-change-icon next-page"
          iconProps={{ iconName: 'ChevronRight' }}
          disabled={currentPage === pageCount}
          title={NEXT_BUTTON}
          aria-label={NEXT_BUTTON}
          onClick={() => { pageIndexChangeHandler(currentPage + 1); }}
        />
      </div>
    </div>
  );
};
